import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	TextField,
	Typography,
	IconButton,
	CircularProgress,
	RadioGroup,
	FormControlLabel,
	Radio,
	FormLabel,
	Chip,
	Slider,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	downloadBase64File,
	exportStage3SignedScanZip,
	getStage3AttestSettings,
	getStage3SignedScanPlan,
	getStage3SignedScanStoredOverview,
	getStage3SignedScanStoredSegment,
	postStage3SignedScanSplit,
	prepareStage3SignedScanEmailCampaign,
	Stage3AttestCohort,
	Stage3SignedScanSegment,
	Stage3SignedScanStoredItem,
} from '../../api/stage3-attestation-api';
import { emailCampaignsPage } from '../../store/fixed-routing';

/** Proporcje strony A4 w pionie (szerokość : wysokość) — wysokość ramki z szerokości jak przy ~60% z małym zapasem. */
const PDF_PREVIEW_ASPECT = '210 / 297';

const DEFAULT_EMAIL_BODY = `Dzień dobry,

W załączeniu przesyłamy zeskanowane podpisane zaświadczenie uczestnictwa w zawodach etapu III Olimpiady Literatury i Języka Polskiego.

Prosimy o potwierdzenie odbioru.

Z poważaniem,
Komitet Główny OLiJP`;

type ScanSource = 'none' | 'stored' | 'split';

type Props = {
	open: boolean;
	onClose: () => void;
	cohort: Stage3AttestCohort;
	cohortLabel: string;
	token: string | null;
};

const fileToBase64 = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const r = new FileReader();
		r.onload = () => {
			const res = r.result;
			if (typeof res !== 'string') {
				reject(new Error('read'));
				return;
			}
			const i = res.indexOf(',');
			resolve(i >= 0 ? res.slice(i + 1) : res);
		};
		r.onerror = () => reject(r.error);
		r.readAsDataURL(file);
	});

const isLikelyPdfFile = (f: File): boolean => {
	if (f.name.toLowerCase().endsWith('.pdf')) {
		return true;
	}
	const t = (f.type || '').toLowerCase();
	return t === 'application/pdf' || t === 'application/x-pdf';
};

function findRowIndexByName(items: { first_name: string; last_name: string }[] | null | undefined, q: string): number {
	if (!items || !q.trim()) {
		return -1;
	}
	const n = q.trim().toLowerCase();
	const tokens = n.split(/\s+/).filter(Boolean);
	return items.findIndex((it) => {
		const full = `${it.last_name} ${it.first_name}`.toLowerCase();
		if (full.includes(n)) {
			return true;
		}
		return tokens.every(
			(t) => full.includes(t) || it.last_name.toLowerCase().includes(t) || it.first_name.toLowerCase().includes(t),
		);
	});
}

const Stage3SignedScanDialog: React.FC<Props> = ({ open, onClose, cohort, cohortLabel, token }) => {
	const navigate = useNavigate();
	const [planLoading, setPlanLoading] = useState(false);
	const [expectedPages, setExpectedPages] = useState<number | null>(null);
	const [planError, setPlanError] = useState<string | null>(null);
	const [storedPartial, setStoredPartial] = useState(false);

	const [scanSource, setScanSource] = useState<ScanSource>('none');
	const [storedItems, setStoredItems] = useState<Stage3SignedScanStoredItem[] | null>(null);

	const [scanEntries, setScanEntries] = useState<{ id: string; name: string; base64: string }[]>([]);
	const [scanOrder, setScanOrder] = useState<'forward' | 'reverse'>('forward');
	const [splitBusy, setSplitBusy] = useState(false);
	const [segments, setSegments] = useState<Stage3SignedScanSegment[] | null>(null);
	const [splitError, setSplitError] = useState<string | null>(null);
	const [previewIndex, setPreviewIndex] = useState(0);
	const [previewBlobUrls, setPreviewBlobUrls] = useState<string[]>([]);
	const blobUrlsRef = useRef<string[]>([]);
	const lazyBlobUrlRef = useRef<string | null>(null);
	const [lazyPreviewUrl, setLazyPreviewUrl] = useState<string | null>(null);
	const [lazySegmentLoading, setLazySegmentLoading] = useState(false);
	const [liveStoredMeta, setLiveStoredMeta] = useState<Stage3SignedScanSegment | null>(null);
	const [previewWidthPct, setPreviewWidthPct] = useState(60);
	const [certYearLabel, setCertYearLabel] = useState(() => String(new Date().getFullYear()));
	/** Szerokość ramki PDF w % szerokości kolumny podglądu (proporcje A4 zachowane). */
	const [pdfFrameScalePct, setPdfFrameScalePct] = useState(88);

	const [jumpRecordStr, setJumpRecordStr] = useState('');
	const [jumpNameQuery, setJumpNameQuery] = useState('');

	const [emailName, setEmailName] = useState('');
	const [emailSubject, setEmailSubject] = useState('Zaświadczenie etap III OLiJP — skan podpisany');
	const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_BODY);
	const [campaignBusy, setCampaignBusy] = useState(false);
	const [campaignId, setCampaignId] = useState<number | null>(null);
	const [zipBusy, setZipBusy] = useState(false);
	const signedScanFileInputRef = useRef<HTMLInputElement>(null);
	const resplitFileInputRef = useRef<HTMLInputElement>(null);
	const [resplitEntries, setResplitEntries] = useState<{ id: string; name: string; base64: string }[]>([]);

	const revokeBlobs = useCallback(() => {
		blobUrlsRef.current.forEach((u) => window.URL.revokeObjectURL(u));
		blobUrlsRef.current = [];
		if (lazyBlobUrlRef.current) {
			window.URL.revokeObjectURL(lazyBlobUrlRef.current);
			lazyBlobUrlRef.current = null;
		}
		setLazyPreviewUrl(null);
	}, []);

	const listLength =
		scanSource === 'stored' ? (storedItems?.length ?? 0) : scanSource === 'split' ? (segments?.length ?? 0) : 0;

	useEffect(() => {
		if (!open || !token) {
			return;
		}
		setPlanLoading(true);
		setPlanError(null);
		setExpectedPages(null);
		setStoredPartial(false);
		void Promise.all([getStage3SignedScanPlan(token, cohort), getStage3SignedScanStoredOverview(token, cohort)])
			.then(([planRes, ovRes]) => {
				setExpectedPages(planRes.data.expected_pages);
				const items = ovRes.data.items ?? [];
				const anyFile = items.some((i) => i.has_file);
				const inSync = ovRes.data.in_sync;
				if (inSync && items.length > 0) {
					setScanSource('stored');
					setStoredItems(items);
					setSegments(null);
					setPreviewIndex(0);
					const ts = new Date().toISOString().slice(0, 10);
					setEmailName(`Zaświadczenia III — skany podpisane — ${cohortLabel} (${ts})`);
				} else {
					setScanSource('none');
					setStoredItems(null);
					setStoredPartial(anyFile && !inSync);
				}
			})
			.catch((err: { response?: { data?: { error?: string } } }) => {
				setPlanError(err.response?.data?.error ?? 'Nie udało się pobrać planu kohorty / stanu skanów.');
			})
			.finally(() => setPlanLoading(false));
	}, [open, token, cohort, cohortLabel]);

	useEffect(() => {
		if (!open || !token) {
			return;
		}
		void getStage3AttestSettings(token)
			.then((res) => {
				const p = res.data.signed_scan_preview_width_pct;
				if (typeof p === 'number' && p >= 50 && p <= 75) {
					setPreviewWidthPct(p);
				} else {
					setPreviewWidthPct(60);
				}
				const cy = (res.data.certificate_year ?? '').trim();
				setCertYearLabel(cy || String(new Date().getFullYear()));
			})
			.catch(() => {
				setPreviewWidthPct(60);
				setCertYearLabel(String(new Date().getFullYear()));
			});
	}, [open, token]);

	useEffect(() => {
		if (!open) {
			revokeBlobs();
			setSegments(null);
			setScanEntries([]);
			setResplitEntries([]);
			setScanOrder('forward');
			setPreviewIndex(0);
			setSplitError(null);
			setCampaignId(null);
			setEmailName('');
			setScanSource('none');
			setStoredItems(null);
			setJumpRecordStr('');
			setJumpNameQuery('');
			setLazySegmentLoading(false);
			setLiveStoredMeta(null);
		}
	}, [open, revokeBlobs]);

	useEffect(() => {
		if (scanSource !== 'split' || !segments || segments.length === 0) {
			if (scanSource !== 'stored') {
				revokeBlobs();
				setPreviewBlobUrls([]);
			}
			return;
		}
		revokeBlobs();
		const urls = segments.map((s) => {
			const bin = atob(s.pdf_base64);
			const bytes = new Uint8Array(bin.length);
			for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
			const blob = new Blob([bytes], { type: 'application/pdf' });
			return window.URL.createObjectURL(blob);
		});
		blobUrlsRef.current = urls;
		setPreviewBlobUrls(urls);
	}, [segments, scanSource, revokeBlobs]);

	useEffect(() => {
		if (scanSource !== 'stored' || !token || !storedItems || storedItems.length === 0) {
			return;
		}
		if (previewIndex < 0 || previewIndex >= storedItems.length) {
			return;
		}
		let cancelled = false;
		setLazySegmentLoading(true);
		setLiveStoredMeta(null);
		if (lazyBlobUrlRef.current) {
			window.URL.revokeObjectURL(lazyBlobUrlRef.current);
			lazyBlobUrlRef.current = null;
		}
		setLazyPreviewUrl(null);
		void getStage3SignedScanStoredSegment(token, cohort, previewIndex)
			.then((res) => {
				if (cancelled) {
					return;
				}
				const s = res.data;
				setLiveStoredMeta({ ...s, pdf_base64: '' });
				const bin = atob(s.pdf_base64);
				const bytes = new Uint8Array(bin.length);
				for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
				const blob = new Blob([bytes], { type: 'application/pdf' });
				const url = window.URL.createObjectURL(blob);
				lazyBlobUrlRef.current = url;
				setLazyPreviewUrl(url);
			})
			.catch((err: unknown) => {
				if (cancelled) {
					return;
				}
				const msg =
					err && typeof err === 'object' && 'response' in err
						? (err as { response?: { data?: { error?: string } } }).response?.data?.error
						: undefined;
				setSplitError(msg ?? 'Nie udało się wczytać skanu z serwera.');
			})
			.finally(() => {
				if (!cancelled) {
					setLazySegmentLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [scanSource, previewIndex, token, cohort, storedItems]);

	const previewUrl = scanSource === 'split' ? (previewBlobUrls[previewIndex] ?? null) : lazyPreviewUrl;

	const displayMeta: Stage3SignedScanSegment | null = useMemo(() => {
		if (scanSource === 'split' && segments?.[previewIndex]) {
			const s = segments[previewIndex];
			return {
				...s,
				second_name: s.second_name ?? '',
				certificate_year: s.certificate_year ?? certYearLabel,
				birth_date_words: s.birth_date_words ?? '',
				certificate_display: s.certificate_display ?? null,
				certificate_number: s.certificate_number ?? null,
			};
		}
		if (scanSource === 'stored' && liveStoredMeta && storedItems?.[previewIndex]) {
			if (liveStoredMeta.student_id === storedItems[previewIndex].student_id) {
				return {
					...liveStoredMeta,
					second_name: liveStoredMeta.second_name ?? '',
					certificate_year: liveStoredMeta.certificate_year ?? certYearLabel,
					birth_date_words: liveStoredMeta.birth_date_words ?? '',
				};
			}
		}
		if (scanSource === 'stored' && storedItems?.[previewIndex]) {
			const it = storedItems[previewIndex];
			return {
				student_id: it.student_id,
				first_name: it.first_name,
				last_name: it.last_name,
				filename: it.filename ?? '',
				pdf_base64: '',
				certificate_number: null,
				certificate_display: null,
				birth_date_words: '',
				second_name: '',
				certificate_year: certYearLabel,
			};
		}
		return null;
	}, [scanSource, segments, previewIndex, liveStoredMeta, storedItems, certYearLabel]);

	const onPickFiles = async (e: React.ChangeEvent<HTMLInputElement>, which: 'main' | 'resplit') => {
		const input = e.target;
		const files = input.files?.length ? Array.from(input.files) : [];
		input.value = '';
		if (files.length === 0) {
			return;
		}
		setSplitError(null);
		const next: { id: string; name: string; base64: string }[] = [];
		for (let i = 0; i < files.length; i++) {
			const f = files[i];
			if (!isLikelyPdfFile(f)) {
				continue;
			}
			try {
				const b64 = await fileToBase64(f);
				const id =
					typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
						? crypto.randomUUID()
						: `f-${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`;
				next.push({ id, name: f.name, base64: b64 });
			} catch {
				setSplitError(`Nie udało się wczytać: ${f.name}`);
				return;
			}
		}
		if (next.length === 0) {
			setSplitError('Brak plików PDF do dodania (oczekiwane rozszerzenie .pdf lub typ application/pdf).');
			return;
		}
		if (which === 'main') {
			setScanEntries((prev) => [...prev, ...next]);
		} else {
			setResplitEntries((prev) => [...prev, ...next]);
		}
	};

	const removeScanAt = (index: number, which: 'main' | 'resplit') => {
		if (which === 'main') {
			setScanEntries((prev) => prev.filter((_, j) => j !== index));
		} else {
			setResplitEntries((prev) => prev.filter((_, j) => j !== index));
		}
	};

	const runSplit = async (entries: { base64: string }[]) => {
		if (!token || entries.length === 0) {
			return;
		}
		setSplitBusy(true);
		setSplitError(null);
		try {
			const res = await postStage3SignedScanSplit(
				token,
				cohort,
				entries.map((e) => e.base64),
				scanOrder,
			);
			setSegments(res.data.segments);
			setScanSource('split');
			setStoredItems(null);
			setPreviewIndex(0);
			setScanEntries([]);
			setResplitEntries([]);
			const ts = new Date().toISOString().slice(0, 10);
			setEmailName(`Zaświadczenia III — skany podpisane — ${cohortLabel} (${ts})`);
			setStoredPartial(false);
		} catch (err: unknown) {
			const msg =
				err && typeof err === 'object' && 'response' in err
					? (err as { response?: { data?: { error?: string } } }).response?.data?.error
					: undefined;
			setSplitError(msg ?? 'Błąd podziału PDF.');
			setSegments(null);
		} finally {
			setSplitBusy(false);
		}
	};

	const onSplitMain = () => void runSplit(scanEntries);
	const onSplitResplit = () => void runSplit(resplitEntries);

	const applyJumpRecord = () => {
		const n = parseInt(jumpRecordStr.replace(/\s/g, ''), 10);
		if (!Number.isFinite(n) || listLength === 0) {
			return;
		}
		setSplitError(null);
		setPreviewIndex(Math.max(0, Math.min(listLength - 1, n - 1)));
	};

	useEffect(() => {
		if (listLength > 0) {
			setJumpRecordStr(String(previewIndex + 1));
		}
	}, [previewIndex, listLength]);

	const applyJumpName = () => {
		const items = scanSource === 'stored' ? storedItems : scanSource === 'split' ? segments : null;
		const idx = findRowIndexByName(items, jumpNameQuery);
		if (idx >= 0) {
			setSplitError(null);
			setPreviewIndex(idx);
		} else {
			setSplitError('Nie znaleziono ucznia pasującego do wpisanego tekstu.');
		}
	};

	const onPrepareCampaign = async () => {
		if (!token) {
			return;
		}
		setCampaignBusy(true);
		try {
			const res = await prepareStage3SignedScanEmailCampaign(token, cohort, emailName, emailSubject, emailBody);
			setCampaignId(res.data.campaign_id);
		} catch (err: unknown) {
			const msg =
				err && typeof err === 'object' && 'response' in err
					? (err as { response?: { data?: { error?: string } } }).response?.data?.error
					: undefined;
			setSplitError(msg ?? 'Nie udało się utworzyć kampanii.');
		} finally {
			setCampaignBusy(false);
		}
	};

	const onDownloadSignedScanZip = async () => {
		if (!token) {
			return;
		}
		setZipBusy(true);
		setSplitError(null);
		try {
			const res = await exportStage3SignedScanZip(token, cohort);
			downloadBase64File(res.data.file_base64, res.data.filename, 'application/zip');
		} catch (err: unknown) {
			const msg =
				err && typeof err === 'object' && 'response' in err
					? (err as { response?: { data?: { error?: string } } }).response?.data?.error
					: undefined;
			setSplitError(msg ?? 'Nie udało się utworzyć archiwum ZIP.');
		} finally {
			setZipBusy(false);
		}
	};

	const showPreview = listLength > 0 && (scanSource === 'split' || scanSource === 'stored');
	const navWait = lazySegmentLoading;
	const certYearShown = (displayMeta?.certificate_year && displayMeta.certificate_year.trim()) || certYearLabel;

	return (
		<Dialog
			open={open}
			onClose={() => !splitBusy && !campaignBusy && !zipBusy && onClose()}
			fullWidth
			maxWidth='lg'
			PaperProps={{
				sx: {
					maxHeight: '94vh',
					display: 'flex',
					flexDirection: 'column',
				},
			}}
		>
			<DialogTitle sx={{ flexShrink: 0 }}>Skany podpisanych zaświadczeń — {cohortLabel}</DialogTitle>
			<DialogContent
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					minHeight: 0,
					flex: '1 1 auto',
					overflowY: 'auto',
					overflowX: 'hidden',
				}}
			>
				{planLoading && <LinearProgress />}
				{planError && (
					<Typography
						color='error'
						variant='body2'
					>
						{planError}
					</Typography>
				)}
				{expectedPages !== null && !planError && (
					<Typography variant='body2'>
						Oczekiwana liczba stron w skanie (uczniowie w kohortcie po wykluczeniach z eksportu zbiorczego):{' '}
						<strong>{expectedPages}</strong>
					</Typography>
				)}
				{storedPartial && scanSource === 'none' && !planLoading && (
					<Alert severity='warning'>
						Część plików jest już na serwerze, ale nie pokrywa całej kohorty (albo brak któregoś skanu). Dodaj PDF i
						wykonaj ponowny podział, aby nadpisać i uzupełnić zestaw.
					</Alert>
				)}
				{scanSource === 'stored' && !planLoading && (
					<Alert severity='success'>
						Na serwerze zapisano komplet skanów dla tej kohorty — możesz od razu przeglądać. Poniżej możesz opcjonalnie
						wgrać nowe PDF i podzielić je ponownie (nadpisze pliki).
					</Alert>
				)}

				{scanSource === 'stored' ? (
					<Accordion
						defaultExpanded={false}
						disableGutters
						sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
					>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Typography variant='subtitle2'>Ponowny podział z nowych PDF (nadpisze pliki na serwerze)</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
								<input
									ref={resplitFileInputRef}
									type='file'
									multiple
									accept='application/pdf,.pdf,application/x-pdf'
									tabIndex={-1}
									style={{ display: 'none' }}
									onChange={(e) => void onPickFiles(e, 'resplit')}
								/>
								<Button
									variant='outlined'
									disabled={!token || splitBusy}
									onClick={() => resplitFileInputRef.current?.click()}
								>
									Dodaj pliki PDF
								</Button>
								{resplitEntries.length > 0 && (
									<Button
										size='small'
										disabled={splitBusy}
										onClick={() => setResplitEntries([])}
									>
										Wyczyść listę
									</Button>
								)}
							</Box>
							{resplitEntries.length > 0 && (
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										gap: 0.5,
										maxHeight: 120,
										overflowY: 'auto',
									}}
								>
									{resplitEntries.map((e, idx) => (
										<Chip
											key={e.id}
											label={`${idx + 1}. ${e.name}`}
											size='small'
											onDelete={splitBusy ? undefined : () => removeScanAt(idx, 'resplit')}
										/>
									))}
								</Box>
							)}
							<FormLabel component='legend'>Kolejność stron względem listy uczniów</FormLabel>
							<RadioGroup
								row
								value={scanOrder}
								onChange={(ev) => setScanOrder(ev.target.value as 'forward' | 'reverse')}
							>
								<FormControlLabel
									value='forward'
									control={<Radio size='small' />}
									label='Zgodnie ze skanem'
								/>
								<FormControlLabel
									value='reverse'
									control={<Radio size='small' />}
									label='Od końca (ostatnia strona → pierwszy uczeń)'
								/>
							</RadioGroup>
							<Button
								variant='contained'
								disabled={!token || resplitEntries.length === 0 || splitBusy}
								onClick={() => void onSplitResplit()}
								sx={{ alignSelf: 'flex-start' }}
							>
								{splitBusy ? <CircularProgress size={22} /> : 'Podziel i zapisz na serwerze'}
							</Button>
						</AccordionDetails>
					</Accordion>
				) : (
					<Box>
						<Typography
							variant='subtitle2'
							gutterBottom
						>
							1. Pliki PDF skanu (wiele plików: strony w kolejności plików — najpierw wszystkie strony pliku 1, potem
							2…)
						</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
							<input
								ref={signedScanFileInputRef}
								type='file'
								multiple
								accept='application/pdf,.pdf,application/x-pdf'
								tabIndex={-1}
								style={{ display: 'none' }}
								onChange={(e) => void onPickFiles(e, 'main')}
							/>
							<Button
								variant='outlined'
								disabled={!token || splitBusy}
								onClick={() => {
									if (!token || splitBusy) {
										return;
									}
									signedScanFileInputRef.current?.click();
								}}
							>
								Dodaj pliki PDF
							</Button>
							{scanEntries.length > 0 && (
								<Button
									size='small'
									disabled={splitBusy}
									onClick={() => setScanEntries([])}
								>
									Wyczyść listę
								</Button>
							)}
						</Box>
						{scanEntries.length > 0 && (
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: 0.5,
									mb: 1,
									maxHeight: 120,
									overflowY: 'auto',
								}}
							>
								{scanEntries.map((e, idx) => (
									<Chip
										key={e.id}
										label={`${idx + 1}. ${e.name}`}
										size='small'
										onDelete={splitBusy ? undefined : () => removeScanAt(idx, 'main')}
									/>
								))}
							</Box>
						)}
						<Box sx={{ mt: 1 }}>
							<FormLabel component='legend'>Kolejność stron względem listy uczniów</FormLabel>
							<RadioGroup
								row
								value={scanOrder}
								onChange={(ev) => setScanOrder(ev.target.value as 'forward' | 'reverse')}
							>
								<FormControlLabel
									value='forward'
									control={<Radio size='small' />}
									label='Zgodnie ze skanem (pierwsza strona → pierwszy uczeń na liście)'
								/>
								<FormControlLabel
									value='reverse'
									control={<Radio size='small' />}
									label='Od końca (ostatnia strona łącznie → pierwszy uczeń)'
								/>
							</RadioGroup>
						</Box>
						<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
							<Button
								variant='contained'
								disabled={!token || scanEntries.length === 0 || splitBusy}
								onClick={() => void onSplitMain()}
							>
								{splitBusy ? <CircularProgress size={22} /> : 'Podziel i zapisz na serwerze'}
							</Button>
						</Box>
					</Box>
				)}

				{splitError && (
					<Typography
						color='error'
						variant='body2'
					>
						{splitError}
					</Typography>
				)}

				{showPreview && (
					<Box
						sx={{
							width: '100%',
							flexShrink: 0,
							display: 'flex',
							flexDirection: 'column',
							gap: 1.5,
							position: 'relative',
							zIndex: 1,
						}}
					>
						<Typography variant='subtitle2'>
							{scanSource === 'stored'
								? 'Podgląd — dokument po dokumencie (z serwera)'
								: 'Podgląd — dokument po dokumencie'}
						</Typography>
						{displayMeta && (
							<Box
								sx={{
									width: '100%',
									minWidth: 0,
									display: 'flex',
									flexDirection: 'column',
									gap: 1.5,
								}}
							>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: 1,
										flexShrink: 0,
									}}
								>
									<Box
										sx={{
											display: 'flex',
											flexWrap: 'wrap',
											alignItems: 'center',
											gap: 0.5,
											columnGap: 1,
											rowGap: 1,
										}}
									>
										<IconButton
											size='small'
											onClick={() => setPreviewIndex(0)}
											disabled={previewIndex <= 0 || navWait}
											aria-label='Pierwszy rekord'
											sx={{ flexShrink: 0 }}
										>
											<KeyboardDoubleArrowLeftIcon fontSize='small' />
										</IconButton>
										<IconButton
											size='small'
											onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
											disabled={previewIndex <= 0 || navWait}
											aria-label='Poprzedni rekord'
											sx={{ flexShrink: 0 }}
										>
											<ChevronLeftIcon fontSize='small' />
										</IconButton>
										<Typography
											variant='body2'
											sx={{ flexShrink: 0, minWidth: '7.5rem', textAlign: 'center' }}
										>
											Rekord {previewIndex + 1} / {listLength}
										</Typography>
										<IconButton
											size='small'
											onClick={() => setPreviewIndex((i) => Math.min(listLength - 1, i + 1))}
											disabled={previewIndex >= listLength - 1 || navWait}
											aria-label='Następny rekord'
											sx={{ flexShrink: 0 }}
										>
											<ChevronRightIcon fontSize='small' />
										</IconButton>
										<IconButton
											size='small'
											onClick={() => setPreviewIndex(listLength - 1)}
											disabled={previewIndex >= listLength - 1 || navWait}
											aria-label='Ostatni rekord'
											sx={{ flexShrink: 0 }}
										>
											<KeyboardDoubleArrowRightIcon fontSize='small' />
										</IconButton>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 1,
												flex: '1 1 200px',
												minWidth: { xs: '100%', sm: 220 },
												maxWidth: 360,
												ml: { xs: 0, sm: 1 },
											}}
										>
											<Typography
												variant='caption'
												color='text.secondary'
												sx={{ flexShrink: 0 }}
											>
												Skala PDF
											</Typography>
											<Slider
												size='small'
												value={pdfFrameScalePct}
												onChange={(_, v) => setPdfFrameScalePct(v as number)}
												min={45}
												max={100}
												step={1}
												valueLabelDisplay='auto'
												valueLabelFormat={(v) => `${v}%`}
												aria-label='Skala podglądu PDF (proporcje A4)'
												sx={{ flex: 1 }}
											/>
										</Box>
									</Box>
									<Box
										sx={{
											display: 'flex',
											flexWrap: 'wrap',
											alignItems: 'center',
											gap: 1,
											width: '100%',
										}}
									>
										<TextField
											size='small'
											label='Nr rekordu'
											value={jumpRecordStr}
											onChange={(e) => setJumpRecordStr(e.target.value)}
											sx={{ width: 120 }}
											inputProps={{ min: 1, max: listLength }}
										/>
										<Button
											size='small'
											variant='outlined'
											disabled={navWait}
											onClick={() => applyJumpRecord()}
										>
											Idź
										</Button>
										<TextField
											size='small'
											label='Szukaj (nazwisko, imię)'
											value={jumpNameQuery}
											onChange={(e) => setJumpNameQuery(e.target.value)}
											sx={{ minWidth: 160, flex: '1 1 140px' }}
										/>
										<Button
											size='small'
											variant='outlined'
											disabled={navWait}
											onClick={() => applyJumpName()}
										>
											Idź do
										</Button>
									</Box>
								</Box>

								<Box
									sx={{
										display: 'flex',
										flexDirection: { xs: 'column', md: 'row' },
										gap: 2,
										alignItems: 'flex-start',
										width: '100%',
										minHeight: { xs: 280, md: 320 },
										maxHeight: { md: 'min(55vh, max(300px, calc(100vh - 380px)))' },
									}}
								>
									<Box
										sx={{
											width: { xs: '100%', md: `${previewWidthPct}%` },
											maxWidth: { xs: '100%', md: `${previewWidthPct}%` },
											flexShrink: 0,
											alignSelf: { md: 'stretch' },
											minHeight: { xs: 240, md: 280 },
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											overflow: 'hidden',
										}}
									>
										{lazySegmentLoading && scanSource === 'stored' && (
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													boxSizing: 'border-box',
													width: `${pdfFrameScalePct}%`,
													minWidth: 200,
													maxWidth: '100%',
													aspectRatio: PDF_PREVIEW_ASPECT,
													minHeight: 240,
													border: '1px solid #ccc',
													borderRadius: 1,
													bgcolor: 'grey.100',
												}}
											>
												<CircularProgress />
											</Box>
										)}
										{previewUrl && !(scanSource === 'stored' && lazySegmentLoading) && (
											<Box
												sx={{
													boxSizing: 'border-box',
													width: `${pdfFrameScalePct}%`,
													minWidth: 200,
													maxWidth: '100%',
													aspectRatio: PDF_PREVIEW_ASPECT,
													minHeight: 240,
													border: '1px solid #ccc',
													borderRadius: 1,
													overflow: 'hidden',
													bgcolor: 'grey.200',
													flexShrink: 0,
												}}
											>
												<Box
													component='iframe'
													title='Podgląd PDF'
													sx={{
														width: '100%',
														height: '100%',
														minHeight: 200,
														border: 'none',
														display: 'block',
													}}
													src={`${previewUrl}#toolbar=0`}
												/>
											</Box>
										)}
										{!previewUrl && !(scanSource === 'stored' && lazySegmentLoading) && (
											<Typography
												variant='body2'
												color='text.secondary'
												sx={{ p: 2, textAlign: 'center' }}
											>
												Brak podglądu PDF (wczytaj rekord lub sprawdź połączenie).
											</Typography>
										)}
									</Box>

									<Box
										sx={{
											flex: 1,
											minWidth: { md: 200 },
											minHeight: { md: 280 },
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'space-between',
											gap: 1.5,
										}}
									>
										<Box
											sx={{
												flex: '0 1 auto',
												minHeight: 120,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												textAlign: 'center',
												px: 1.5,
												py: 1.5,
												border: '1px solid',
												borderColor: 'divider',
												borderRadius: 1,
												overflow: 'hidden',
											}}
										>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'center',
													justifyContent: 'center',
													gap: 0.75,
													width: '100%',
												}}
											>
												<Typography sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.35 }}>
													{displayMeta.first_name}
												</Typography>
												<Typography sx={{ fontWeight: 500, fontSize: '0.95rem', lineHeight: 1.35 }}>
													{(displayMeta.second_name || '').trim() ? displayMeta.second_name : '—'}
												</Typography>
												<Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.35 }}>
													{displayMeta.last_name}
												</Typography>
												<Box sx={{ height: 8 }} />
												<Typography
													variant='body2'
													color='text.secondary'
													sx={{ lineHeight: 1.4 }}
												>
													{displayMeta.birth_date_words?.trim() ? displayMeta.birth_date_words : '—'}
												</Typography>
												<Typography
													variant='body2'
													sx={{ mt: 0.5 }}
												>
													Id ucznia: {displayMeta.student_id}
												</Typography>
											</Box>
										</Box>

										<Box
											sx={{
												flex: '0 0 auto',
												border: '1px solid',
												borderColor: 'divider',
												borderRadius: 1,
												p: 1.5,
												textAlign: 'left',
											}}
										>
											<Typography
												variant='body2'
												sx={{ lineHeight: 1.5 }}
											>
												Nr zaświadczenia:{' '}
												<Box
													component='span'
													sx={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}
												>
													{displayMeta.certificate_display ?? '—'}
												</Box>
												<Box
													component='span'
													color='text.secondary'
												>
													/{certYearShown}
												</Box>
											</Typography>
											<Typography
												variant='caption'
												color='text.secondary'
												sx={{
													fontFamily: 'ui-monospace, monospace',
													display: 'block',
													mt: 1.25,
													wordBreak: 'break-all',
													lineHeight: 1.4,
												}}
											>
												{displayMeta.filename || '—'}
											</Typography>
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'center',
													gap: 0.5,
													mt: 1.5,
												}}
											>
												<IconButton
													size='small'
													onClick={() => setPreviewIndex(0)}
													disabled={previewIndex <= 0 || navWait}
													aria-label='Pierwszy dokument'
												>
													<KeyboardDoubleArrowLeftIcon fontSize='small' />
												</IconButton>
												<IconButton
													size='small'
													onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
													disabled={previewIndex <= 0 || navWait}
													aria-label='Poprzedni dokument'
												>
													<ChevronLeftIcon fontSize='small' />
												</IconButton>
												<IconButton
													size='small'
													onClick={() => setPreviewIndex((i) => Math.min(listLength - 1, i + 1))}
													disabled={previewIndex >= listLength - 1 || navWait}
													aria-label='Następny dokument'
												>
													<ChevronRightIcon fontSize='small' />
												</IconButton>
												<IconButton
													size='small'
													onClick={() => setPreviewIndex(listLength - 1)}
													disabled={previewIndex >= listLength - 1 || navWait}
													aria-label='Ostatni dokument'
												>
													<KeyboardDoubleArrowRightIcon fontSize='small' />
												</IconButton>
											</Box>
										</Box>
									</Box>
								</Box>
							</Box>
						)}
					</Box>
				)}

				<Box
					sx={{
						width: '100%',
						flexShrink: 0,
						mt: showPreview ? 2 : 0,
						pt: showPreview ? 2 : 0,
						borderTop: showPreview ? 1 : 0,
						borderColor: 'divider',
						position: 'relative',
						zIndex: 0,
					}}
				>
					<Typography variant='subtitle2'>2. Archiwum ZIP (wszystkie skany z serwera dla kohorty)</Typography>
					<Typography
						variant='body2'
						color='text.secondary'
					>
						Pakuje pliki PDF zapisane na serwerze (kolejność jak na liście uczniów). Uczniowie bez skanu są pomijani.
					</Typography>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1 }}>
						<Button
							variant='outlined'
							disabled={!token || zipBusy}
							onClick={() => void onDownloadSignedScanZip()}
						>
							{zipBusy ? <CircularProgress size={22} /> : 'Pobierz ZIP ze skanów (serwer)'}
						</Button>
					</Box>
				</Box>

				<Typography variant='subtitle2'>3. Wstępna kampania e-mail (moduł Wysyłki)</Typography>
				<TextField
					fullWidth
					size='small'
					label='Nazwa kampanii'
					value={emailName}
					onChange={(e) => setEmailName(e.target.value)}
				/>
				<TextField
					fullWidth
					size='small'
					label='Temat'
					value={emailSubject}
					onChange={(e) => setEmailSubject(e.target.value)}
				/>
				<TextField
					fullWidth
					multiline
					minRows={6}
					label='Treść (HTML — możesz edytować później w Wysyłkach)'
					value={emailBody}
					onChange={(e) => setEmailBody(e.target.value)}
				/>
				<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
					<Button
						variant='contained'
						color='secondary'
						disabled={!token || campaignBusy || !emailName.trim() || !emailSubject.trim()}
						onClick={() => void onPrepareCampaign()}
					>
						{campaignBusy ? <CircularProgress size={22} /> : 'Utwórz kampanię (odbiorcy + załączniki PDF)'}
					</Button>
					<Button
						variant='outlined'
						onClick={() => navigate(emailCampaignsPage)}
					>
						Otwórz moduł Wysyłki
					</Button>
				</Box>
				{campaignId !== null && (
					<Typography
						variant='body2'
						color='success.main'
					>
						Utworzono kampanię #{campaignId}. W module Wysyłki możesz uzupełnić treść i uruchomić wysyłkę.
					</Typography>
				)}
			</DialogContent>
			<DialogActions sx={{ flexShrink: 0 }}>
				<Button
					onClick={onClose}
					disabled={splitBusy || campaignBusy || zipBusy}
				>
					Zamknij
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default Stage3SignedScanDialog;
